// =============================================================================
// GIVEAWAY WHEEL — THE ONE RENDERER
// -----------------------------------------------------------------------------
// The host's panel, the live page and the projector all draw the same wheel and
// must all stop on the same segment. The math lived in two copies (host +
// live) and the projector was about to make three; now `drawWheel` / `animateWheel`
// are here and each screen owns only its DOM.
//
//   MHVGiveawayWheel.drawWheel(canvas, participants, highlightIndex, rotation)
//   MHVGiveawayWheel.animateWheel(canvas, participants, winnerIndex, fullSpins, opts)
//     → { cancel() }   opts: { onTick(segIdx, nearBoundary), onComplete() }
//   MHVGiveawayWheel.createOverlay(root)  — the full-screen wheel + winner card
//     used by the live page and the projector: { spin(data), winner(data), hide() }
//
// A participant is { label|screenName, paddleNumber?, count? }. ⚠️ At an
// IN-PERSON sale the segment prints the PADDLE under the name as "#12" — a room
// knows the number on the card in their hand, never a bidder number — so every
// screen shows "First L." + "#12", the same way (owner: never the word "Paddle").
//
// ⚠️ The animation completes on a wall clock, not only on requestAnimationFrame:
// a background tab throttles rAF, and a host who switched windows mid-spin used
// to leave every screen spinning with no winner ever announced.
// =============================================================================
(function () {
  'use strict';

  var COLORS = [
    '#FF00C2', '#8B00FF', '#00C8FF', '#00FF88', '#FFD700',
    '#FF4444', '#FF8800', '#44AAFF', '#AA44FF', '#44FF88',
    '#FF6699', '#6699FF', '#66FF99', '#FF9966', '#9966FF'
  ];
  var DURATION = 10000;

  function labelOf(p) {
    if (!p) return '';
    if (typeof p === 'string') return p;
    return p.label || p.displayName || p.screenName || '';
  }
  function paddleOf(p) { return (p && typeof p === 'object' && p.paddleNumber) ? String(p.paddleNumber) : ''; }
  function countOf(p) { return (p && typeof p === 'object' && Number(p.count) > 0) ? Number(p.count) : 1; }

  // Proportional arcs — a buyer with three lots gets three lots' worth of wheel.
  function computeArcs(participants) {
    var total = 0, i;
    for (i = 0; i < participants.length; i++) total += countOf(participants[i]);
    if (total === 0) total = participants.length || 1;
    var arcs = [];
    for (i = 0; i < participants.length; i++) arcs.push((countOf(participants[i]) / total) * Math.PI * 2);
    return arcs;
  }

  function drawWheel(canvas, participants, highlightIndex, rotation) {
    var ctx = canvas.getContext('2d');
    var cx = canvas.width / 2, cy = canvas.height / 2;
    var radius = Math.min(cx, cy) - 10;
    var n = participants.length;
    var arcs = computeArcs(participants);
    // Type scales with the canvas: the host's 300px panel wheel and a 1080p wall
    // are the same drawing at different sizes.
    var base = Math.max(10, Math.round(radius / 12));
    var fontPx = n > 12 ? base * 0.8 : n > 8 ? base * 0.9 : base;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    var angle = rotation;
    for (var i = 0; i < n; i++) {
      var start = angle, end = angle + arcs[i];
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, radius, start, end);
      ctx.closePath();
      ctx.fillStyle = COLORS[i % COLORS.length];
      if (highlightIndex === i) {
        var grad = ctx.createLinearGradient(cx + radius * Math.cos(start), cy + radius * Math.sin(start), cx + radius * Math.cos(end), cy + radius * Math.sin(end));
        grad.addColorStop(0, '#FF0000'); grad.addColorStop(0.17, '#FF8800'); grad.addColorStop(0.33, '#FFE500');
        grad.addColorStop(0.5, '#00FF44'); grad.addColorStop(0.67, '#00AAFF'); grad.addColorStop(0.83, '#8800FF'); grad.addColorStop(1, '#FF00CC');
        ctx.fillStyle = grad;
      }
      ctx.fill();
      if (highlightIndex === i) { ctx.strokeStyle = '#fff'; ctx.lineWidth = 3; ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 12; }
      else { ctx.strokeStyle = '#000'; ctx.lineWidth = 1; }
      ctx.stroke();
      ctx.shadowBlur = 0;

      if (arcs[i] > 0.15) { // ~8.6° — below that there is no room for type
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(start + arcs[i] / 2);
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#fff';
        var name = labelOf(participants[i]);
        if (name.length > 14) name = name.substring(0, 12) + '..';
        var paddle = paddleOf(participants[i]);
        if (paddle && arcs[i] > 0.3) {
          // Two lines: the name, and the paddle the room can see in their hand.
          ctx.font = 'bold ' + fontPx + 'px sans-serif';
          ctx.fillText(name, radius - 8, -fontPx * 0.55);
          ctx.font = (fontPx * 0.85) + 'px sans-serif';
          ctx.fillStyle = 'rgba(255,255,255,0.9)';
          ctx.fillText('#' + paddle, radius - 8, fontPx * 0.6);
        } else {
          ctx.font = 'bold ' + fontPx + 'px sans-serif';
          ctx.fillText(paddle ? (name + ' #' + paddle) : name, radius - 8, 0);
        }
        ctx.restore();
      }
      angle = end;
    }
    // Hub
    ctx.beginPath();
    ctx.arc(cx, cy, Math.max(14, radius * 0.07), 0, Math.PI * 2);
    ctx.fillStyle = '#1a1a1a';
    ctx.fill();
    ctx.strokeStyle = '#FF00C2';
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  // Which segment sits under the ticker (top) at this rotation, and whether the
  // ticker is close enough to a boundary to "tick".
  function segmentAtTop(arcs, rotation) {
    var topAngle = (-rotation - Math.PI / 2) % (Math.PI * 2);
    if (topAngle < 0) topAngle += Math.PI * 2;
    var bounds = [0], seg = 0, k;
    for (k = 0; k < arcs.length; k++) bounds.push(bounds[k] + arcs[k]);
    for (k = 0; k < bounds.length - 1; k++) { if (topAngle < bounds[k + 1]) { seg = k; break; } }
    var near = false;
    for (k = 0; k < bounds.length; k++) {
      var d = Math.abs(topAngle - bounds[k]);
      if (d > Math.PI) d = Math.PI * 2 - d;
      if (d < 0.06) { near = true; break; }
    }
    return { index: seg, nearBoundary: near };
  }

  function animateWheel(canvas, participants, winnerIndex, fullSpins, opts) {
    opts = opts || {};
    var arcs = computeArcs(participants);
    var before = 0;
    for (var i = 0; i < winnerIndex; i++) before += arcs[i];
    var winnerCenter = before + arcs[winnerIndex] / 2;
    // Winner centre lands at the top (-π/2) after `fullSpins` full turns.
    var target = -Math.PI / 2 - winnerCenter - (fullSpins || 10) * Math.PI * 2;

    var finished = false, rafId = null, fallbackId = null, startTime = null;
    function ease(t) { return 1 - Math.pow(1 - t, 3); }
    function clear() {
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      if (fallbackId) { clearTimeout(fallbackId); fallbackId = null; }
    }
    function finish() {
      if (finished) return;
      finished = true;
      clear();
      if (opts.onComplete) opts.onComplete();
    }
    function frame(ts) {
      if (finished) return;
      if (!startTime) startTime = ts;
      var progress = Math.min((ts - startTime) / DURATION, 1);
      var rotation = target * ease(progress);
      drawWheel(canvas, participants, progress >= 1 ? winnerIndex : -1, rotation);
      if (opts.onTick) {
        var at = segmentAtTop(arcs, rotation);
        opts.onTick(at.index, progress < 1 && at.nearBoundary, progress);
      }
      if (progress < 1) rafId = requestAnimationFrame(frame);
      else finish();
    }
    fallbackId = setTimeout(function () {
      try { drawWheel(canvas, participants, winnerIndex, target); } catch (e) { /* ignore */ }
      if (opts.onTick) opts.onTick(winnerIndex, false, 1);
      finish();
    }, DURATION + 750);
    rafId = requestAnimationFrame(frame);

    return {
      // A takeover: this screen no longer owns the spin. Stops everything and
      // suppresses onComplete, so a host who lost control cannot announce a winner.
      cancel: function () { finished = true; clear(); }
    };
  }

  function esc(s) { return (window.MHV && window.MHV.escapeHtml) ? window.MHV.escapeHtml(s) : String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function escAttr(s) { return (window.MHV && window.MHV.escapeAttr) ? window.MHV.escapeAttr(s) : esc(s); }

  // The winner's name as the room should read it. Privacy-aware via the shared
  // resolver when it is loaded; the desk's label otherwise.
  function winnerName(data) {
    if (window.MHV && window.MHV.chatDisplayName && (data.firstName || data.screenName)) {
      try {
        var r = window.MHV.chatDisplayName({ screenName: data.screenName, firstName: data.firstName, lastName: data.lastName, bidderNumber: data.bidderNumber, hideName: data.hideName, isAdminView: false });
        if (r && r.primary) return r.primary;
      } catch (e) { /* fall through */ }
    }
    return data.displayName || data.screenName || 'Winner';
  }

  // ---------------------------------------------------------------------------
  // The full-screen overlay (live page + projector). Builds its own DOM inside
  // `root` (created on document.body when omitted) so a page adds one line.
  // ---------------------------------------------------------------------------
  function createOverlay(root, opts) {
    opts = opts || {};
    if (!root) {
      root = document.createElement('div');
      root.className = 'giveaway-wheel-overlay';
      root.style.display = 'none';
      document.body.appendChild(root);
    }
    root.classList.add('giveaway-wheel-overlay');
    root.innerHTML =
      '<div class="giveaway-wheel-inner">' +
        '<div class="giveaway-live-heading" id="giveaway-live-heading" style="display:none"></div>' +
        '<div class="giveaway-wheel-name" id="giveaway-live-name"></div>' +
        '<div class="giveaway-canvas-wrap">' +
          '<canvas id="giveaway-live-canvas" width="' + (opts.size || 400) + '" height="' + (opts.size || 400) + '"></canvas>' +
          '<div class="giveaway-live-ticker">&nbsp;</div>' +
        '</div>' +
      '</div>' +
      '<div id="giveaway-live-winner" class="giveaway-live-winner" style="display:none"></div>';
    var canvas = root.querySelector('canvas');
    var heading = root.querySelector('.giveaway-live-heading');
    var nameEl = root.querySelector('.giveaway-wheel-name');
    var ticker = root.querySelector('.giveaway-live-ticker');
    var winnerEl = root.querySelector('.giveaway-live-winner');
    var spinning = false, pendingWinner = null, dismissTimer = null, run = null;
    var movedFrom = null;

    function hide() {
      root.style.display = 'none';
      winnerEl.style.display = 'none';
      if (movedFrom && root.parentElement === document.body) { movedFrom.appendChild(root); movedFrom = null; }
      if (dismissTimer) { clearTimeout(dismissTimer); dismissTimer = null; }
    }

    function showWinner(data) {
      var initials = (winnerName(data) || '?').substring(0, 2).toUpperCase();
      var html = '<div class="giveaway-live-winner-card">';
      html += '<div class="giveaway-live-winner-avatar">';
      if (data.profileImage) {
        html += '<img src="' + escAttr(data.profileImage) + '" alt="" onerror="this.style.display=\'none\';this.nextElementSibling.style.display=\'flex\';">';
        html += '<div class="giveaway-live-avatar-fallback" style="display:none;">' + esc(initials) + '</div>';
      } else {
        html += '<div class="giveaway-live-avatar-fallback">' + esc(initials) + '</div>';
      }
      html += '</div>';
      html += '<div class="giveaway-live-winner-name">' + esc(winnerName(data)) + '</div>';
      if (data.paddleNumber) html += '<div class="giveaway-live-winner-paddle">#' + esc(String(data.paddleNumber)) + '</div>';
      html += '<div class="giveaway-live-winner-prize">WINS $' + esc(String(data.amount || 0)) + ' OFF!</div>';
      html += '</div>';
      winnerEl.innerHTML = html;
      winnerEl.style.display = 'flex';
      if (opts.onWinner) { try { opts.onWinner(data); } catch (e) { /* ignore */ } }
      if (dismissTimer) clearTimeout(dismissTimer);
      dismissTimer = setTimeout(hide, opts.dismissMs || 8000);
    }

    return {
      spin: function (data) {
        var participants = (data && data.participants) || [];
        if (!participants.length) return;
        if (dismissTimer) { clearTimeout(dismissTimer); dismissTimer = null; }
        if (run) run.cancel();
        pendingWinner = null;
        spinning = true;
        // Mobile live page: out of the video's stacking context.
        if (opts.moveToBody && root.parentElement !== document.body) { movedFrom = root.parentElement; document.body.appendChild(root); }
        root.style.display = 'flex';
        winnerEl.style.display = 'none';
        heading.innerHTML = '<h2>🎁 Giveaway</h2><div class="giveaway-live-subtitle">for $' + esc(String(data.amount || 0)) + ' OFF</div>';
        heading.style.display = '';
        nameEl.textContent = '';
        nameEl.style.display = '';
        var lastSeg = -1;
        drawWheel(canvas, participants, -1, 0);
        run = animateWheel(canvas, participants, data.winnerIndex || 0, data.fullSpins || 10, {
          onTick: function (seg, near, progress) {
            if (ticker) ticker.classList.toggle('tick', !!near);
            if (seg !== lastSeg) {
              lastSeg = seg;
              var p = participants[seg];
              nameEl.textContent = labelOf(p) + (paddleOf(p) ? ' #' + paddleOf(p) : '');
              nameEl.style.color = COLORS[seg % COLORS.length];
              nameEl.classList.remove('giveaway-name-flash');
              void nameEl.offsetWidth;
              nameEl.classList.add('giveaway-name-flash');
            }
            if (progress >= 1) { heading.style.display = 'none'; nameEl.style.display = 'none'; }
          },
          onComplete: function () {
            spinning = false;
            run = null;
            if (pendingWinner) { showWinner(pendingWinner); pendingWinner = null; }
          }
        });
      },
      winner: function (data) {
        if (spinning) { pendingWinner = data; return; }
        showWinner(data);
      },
      hide: hide,
      root: root
    };
  }

  window.MHVGiveawayWheel = {
    COLORS: COLORS,
    DURATION: DURATION,
    computeArcs: computeArcs,
    drawWheel: drawWheel,
    animateWheel: animateWheel,
    createOverlay: createOverlay,
    labelOf: labelOf,
    winnerName: winnerName
  };
})();
